/**
 * What a GoTrue sign-in link brings back to the portal (design section 2, F1), read from the URL.
 *
 * The portal client runs the PKCE flow (`lib/supabase.ts`): the link returns to `/signin` or
 * `/invite/<id>` with `?code=<auth code>&sb_flow_id=<flow>`, and only the browser that asked for the link
 * holds the code verifier the exchange needs. So a link crafted by someone else cannot sign this browser
 * into his account (login CSRF). Implicit-flow tokens in the fragment (`#access_token=..&refresh_token=..`)
 * are NEVER a session: whatever path they arrive on, they are stripped and ignored. A failed link comes
 * back as `?error=..&error_code=..` (and the same in the fragment); its description is English for
 * developers: console.warn only.
 */
import type { AuthErrorKey, AuthLikeError } from './authErrors';

export type AuthLink =
  | { kind: 'none' }
  | { kind: 'code'; code: string; flowId: string | null }
  | { kind: 'error'; code: string; error: string; description: string };

/** Query parameters of a returning link, removed from the address bar on a landing path. */
const LINK_QUERY_PARAMS = ['code', 'sb_flow_id', 'error', 'error_code', 'error_description'];
/** A fragment carrying any of these is GoTrue's (tokens or an error): removed whole, wherever it lands. */
const GOTRUE_FRAGMENT_PARAMS = ['access_token', 'refresh_token', 'provider_token', 'provider_refresh_token', 'error', 'error_code', 'error_description'];

/** The only two places a sign-in link completes: `/signin` and `/invite/<id>`. */
export function isLinkLandingPath(pathname: string): boolean {
  return pathname === '/signin' || /^\/invite\/[^/]+$/.test(pathname);
}

/**
 * Where a returning code or error is consumed: the two landing paths, and `/settings/profile`, where a
 * `linkIdentity` round trip comes back. Not a landing path: nothing sends a sign-in there.
 */
export function isAuthReturnPath(pathname: string): boolean {
  return isLinkLandingPath(pathname) || pathname === '/settings/profile';
}

function fragmentParams(hash: string): URLSearchParams | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.includes('=')) return null;
  const params = new URLSearchParams(raw);
  return GOTRUE_FRAGMENT_PARAMS.some((k) => params.has(k)) ? params : null;
}

/** True when the hash is GoTrue's (tokens or an error), never a plain `#section`. */
export function isGoTrueFragment(hash: string): boolean {
  return fragmentParams(hash) !== null;
}

function errorOf(params: URLSearchParams | null): { code: string; error: string; description: string } | null {
  if (!params) return null;
  const error = params.get('error');
  const errorCode = params.get('error_code');
  if (!error && !errorCode) return null;
  const code = errorCode || error || 'invalid_link';
  return { code, error: error || code, description: params.get('error_description') ?? '' };
}

/**
 * The link in `url` and the URL to replace it with (`cleanUrl`, null when nothing must go). A code or
 * an error counts only where one returns (`isAuthReturnPath`); GoTrue's fragment is stripped everywhere.
 */
export function readAuthLink(url: { pathname: string; search: string; hash: string }): { link: AuthLink; cleanUrl: string | null } {
  const fragment = fragmentParams(url.hash);
  const hash = fragment ? '' : url.hash;
  let search = url.search;
  let link: AuthLink = { kind: 'none' };

  if (isAuthReturnPath(url.pathname)) {
    const query = new URLSearchParams(url.search);
    const error = errorOf(query) ?? errorOf(fragment);
    const code = query.get('code');
    if (error) link = { kind: 'error', ...error };
    else if (code) link = { kind: 'code', code, flowId: query.get('sb_flow_id') || null };
    if (LINK_QUERY_PARAMS.some((k) => query.has(k))) {
      for (const k of LINK_QUERY_PARAMS) query.delete(k);
      const rest = query.toString();
      search = rest ? `?${rest}` : '';
    }
  }

  const changed = search !== url.search || hash !== url.hash;
  return { link, cleanUrl: changed ? url.pathname + search + hash : null };
}

/** `mallory@evil.test` -> `m•••@evil.test`: enough to recognise one's own address, not to read another's. */
export function maskEmail(email: string | null | undefined): string {
  const at = email ? email.indexOf('@') : -1;
  if (!email || at < 1) return '•••';
  return `${email[0]}•••${email.slice(at)}`;
}

/** A refused exchange: `other_browser` when this browser holds no (or another flow's) verifier, else `invalid_link`. */
export function linkFailureCode(err: AuthLikeError): 'other_browser' | 'invalid_link' {
  if (err.code === 'pkce_code_verifier_not_found' || err.name === 'AuthPKCECodeVerifierMissingError' || err.code === 'bad_code_verifier') {
    return 'other_browser';
  }
  return 'invalid_link';
}

/** The `auth.errors.*` copy of a link error code. */
export function linkErrorKey(code: string): AuthErrorKey {
  if (code === 'other_browser') return 'linkOtherBrowser';
  if (code === 'other_account') return 'otherAccount';
  return 'linkExpired';
}


const OAUTH_UNAVAILABLE_CODES = new Set(['manual_linking_disabled', 'provider_disabled', 'validation_failed']);

/**
 * The `auth.errors.*` copy of an OAuth return error (the portal's own marker says it was one, see
 * `oauthPending.ts`). `code` is GoTrue's `error_code` (or the portal's `other_*`), `error` the raw param.
 */
export function oauthErrorKey(link: { code: string; error?: string }): AuthErrorKey {
  if (link.code === 'other_account') return 'otherAccountOAuth';
  if (link.code === 'other_browser') return 'linkOtherBrowser';
  if (link.code === 'identity_already_exists') return 'identityTaken';
  if (OAUTH_UNAVAILABLE_CODES.has(link.code)) return 'oauthUnavailable';
  if (link.code === 'access_denied' || link.error === 'access_denied') return 'oauthCancelled';
  return 'oauthFailed';
}
